import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {IDoopOrder, SearchBy} from '../Interfaces/IDOOP';
import {optionsOISList} from '../constants';
import {getOrderedItemsForProject} from '../BackendFacade/apiCalls';
import {useMain} from '../../../Utils/SessionControls/mainContext';
import {HubPathRouting} from '../../../HubFramework/pathBuilder';
import OdxAreaHeader from '../../../OdxComponents/NativeOdx/Odx-AreaHeader';
import OdxSearchbar from '../../../OdxComponents/NativeOdx/Odx-Searchbar';
import OdxSelect, {OdxSelectOptionProps} from '../../../OdxComponents/NativeOdx/FormControls/Odx-Select';
import {odxIconSets} from '../../../OdxComponents/constants';
import OdxInputField from '../../../OdxComponents/NativeOdx/FormControls/Odx-InputField';
import OdxIcon from '../../../OdxComponents/NativeOdx/Odx-Icon';
import OdxTable from '../../../OdxComponents/NativeOdx/Table/Odx-Table';
import {OdxTableRow} from '../../../OdxComponents/NativeOdx/Table/Odx-TableRow';
import {OdxTableCell} from '../../../OdxComponents/NativeOdx/Table/Odx-Tablecell';
import OdxMainFilterGroup from '../../../OdxComponents/NativeOdx/Odx-MainFilterGroup';
import Logs from './Modal/Logs';
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {downloadListObjectAsCSV} from "../../../Utils/objectDownload";
import {orderBy} from "../../../Utils/sorting";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {useDoopData} from "../doopDataContext";

interface LogEvent {
    date: string;
    time: string;
    level: string;
    message: string;
}

const Overview: React.FC = () => {
    const [areas, setAreas] = useState<string[]>([]);
    const [fromDate, setFromDate] = useState('2021-01-01');
    const [doopOrders, setDoopOrders] = useState<IDoopOrder[]>([]);
    const [searchBy, setSearchBy] = useState(SearchBy.LastModified);
    const [sortAscend, setSortAscend] = useState(false);
    const [sortBy, setSortBy] = useState('name');
    const [states, setStates] = useState<string[]>([]);
    const [toDate, setToDate] = useState(new Date().toISOString().substring(0, 10));
    const [searchText, setSearchText] = useState('');
    const {areas: availableAreas, meta, error} = useDoopData();
    const baseId = 'doop-overview';

    const {setLoading, createToast} = useMain();
    const setLoadingRef = useRef(setLoading);
    useEffect(() => {
        setLoadingRef.current = setLoading;
    }, [setLoading]);

    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [currentLogs, setCurrentLogs] = useState<LogEvent[]>([]);

    useModuleViewTracking('doop-overview')

    const getProjectData = useCallback(async () => {
        let orderResp = await getOrderedItemsForProject(
            HubPathRouting.currentProject.id,
            HubPathRouting.currentModule.id
        );

        let orders = orderResp.data;

        // replace all 1970 dates from donedate and modified date with null
        orders = orders.map((item) => {
            if (item.dateDone && item.dateDone.indexOf('1970') !== -1) {
                item.dateDone = "";
            }
            if (item.dateModified && item.dateModified.indexOf('1970') !== -1) {
                item.dateModified = "";
            }
            return item;
        });

        setDoopOrders(orders);
    }, []);

    useEffect(() => {
        setLoadingRef.current(true);
        getProjectData().finally(() => {
            setLoadingRef.current(false);
        });
    }, [getProjectData]);

    useEffect(() => {
        setDoopOrders(orderBy(doopOrders, order => order[sortBy] , sortAscend, order => order.id));
    }, [sortBy, sortAscend]);

    const filteredProjects = () => {
        if (!doopOrders) return [];
        return doopOrders
            .filter((item) => {
                const dateToCompare = searchBy === SearchBy.LastModified ? item.dateModified : item.dateDone;
                if (fromDate && toDate) {
                    return dateToCompare.substring(0, 10) >= fromDate && dateToCompare.substring(0, 10) <= toDate;
                } else {
                    return true;
                }
            })
            .filter((item) => {
                if (states.length > 0) {
                    return states.includes(item.journeyState);
                } else {
                    return true;
                }
            })
            .filter((item) => {
                if (areas.length > 0) {
                    return areas.includes(item.area);
                } else {
                    return true;
                }
            })
            .filter((item) => {
                if (searchText.trim() !== '') {
                    return item.name.toLowerCase().includes(searchText.toLowerCase());
                } else {
                    return true;
                }
            });
    };

    const resetView = () => {
        getProjectData();
        setStates([]);
        setFromDate('2021-01-01');
        setToDate(new Date().toISOString().substring(0, 10));
        setAreas([]);
        setSearchText('');
        setSearchBy(SearchBy.LastModified);
        createToast({message: "Refresh successfully", variant: "success", title: "Success"});
    };

    const downloadView = () => {
        const data = filteredProjects().map((item) => {
            return {
                Name: item.name,
                URI: item.id,
                OrderNumber: item.orderNumber,
                State: item.journeyState,
                'Estimated Hours': item.estimatedHours,
                'Acceptance Date': trimDate(item.dateDone),
            };
        });
        const additionalData = 'Sum of all shown entries;' + sumOfAllShownProjects() + ' h';
        downloadListObjectAsCSV(data, additionalData, HubPathRouting.currentProject.id + '_overview.csv');
    };

    const sumOfAllShownProjects = () => {
        const projects = filteredProjects();
        if (projects.length === 0) {
            return 0;
        }
        const total = projects.reduce((acc, project) => acc + (project.estimatedHours || 0), 0);
        return Math.round(total * 100) / 100;
    };

    const getCurrentDoopAreasOverview = () => {
        let optionList: OdxSelectOptionProps[] = [];
        if (availableAreas) {
            optionList = availableAreas.map((area) => {
                const split = area.name.split('\\');
                const name = split[split.length - 1];
                return {label: name, value: area.name};
            });
        }
        return optionList;
    };

    const trimDate = (date: string) => {
        if (date.indexOf('1970') !== -1) {
            return '';
        }
        return date.substring(0, 10);
    };

    const LogPopupShow = (logData: string) => {
        const parsedLogs: LogEvent[] = [];

        if (logData) {
            const entries = logData.split('<br><br>').filter(entry => entry.trim() !== '');

            entries.forEach(entry => {
                const lines = entry.split('<br>');

                if (lines.length >= 2) {
                    const firstLine = lines[0];
                    const message = lines.slice(1).join('\n');

                    const levelMatch = firstLine.match(/^(INFO|ERROR|WARN): (\d{1,2}\/\d{1,2}\/\d{4}) (\d{1,2}:\d{2}:\d{2} (?:AM|PM))/);
                    if (levelMatch) {
                        const [, level, date, time] = levelMatch;
                        parsedLogs.push({
                            level,
                            date,
                            time,
                            message,
                        });
                    }
                }
            });
        }

        setCurrentLogs(parsedLogs);
        setIsLogsOpen(true);
    };

    if (error) {
        return <div>{error}</div>;
    }

    if (meta === null) {
        return <></>;
    }

    // Options for OdxSelect to choose between Last Modified and Acceptance Date
    const searchOptions: OdxSelectOptionProps[] = [
        {label: 'Last Modified', value: SearchBy.LastModified},
        {label: 'Acceptance Date', value: SearchBy.AcceptanceDate},
    ];

    return (
        <div id={`${baseId}-view`}>
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>

            <Logs
                logs={currentLogs}
                isOpen={isLogsOpen}
                onClose={() => setIsLogsOpen(false)}
                idPrefix={`${baseId}-logs`}
            />
            <div className="p-4">
                <OdxAreaHeader text="Orders" odxSize="medium" className={'!pl-0 !pr-0'} id={`${baseId}-header`}/>
                <OdxMainFilterGroup>
                    <div className="flex-grow odx-padding-top-16 pb-5">
                        <div style={{marginTop: '-7px'}}>
                            <OdxSearchbar onSearch={(term) => setSearchText(term)} id={`${baseId}-search`}/>
                        </div>
                    </div>
                    <div style={{marginTop: '-4px'}}>
                        <div className="flex items-center space-x-4">
                            <div style={{marginTop: '-24px'}}>
                                <OdxSelect
                                    options={searchOptions}
                                    id={`${baseId}-search-by`}
                                    value={searchOptions.find(x => x.value === searchBy).value}
                                    onChange={(selectedValue: ChangeEvent<HTMLSelectElement>) => setSearchBy(selectedValue.target.value as SearchBy)}
                                    placeholder="Search By"
                                    isMulti={false}
                                />
                            </div>
                            <div style={{marginTop: '-24px'}}>
                                <OdxSelect
                                    options={optionsOISList}
                                    value={states}
                                    id={`${baseId}-state-filter`}
                                    onChange={(selectedValues) => {
                                        setStates(Array.isArray(selectedValues) ? selectedValues : []);
                                    }}
                                    placeholder="State"
                                    isMulti={true}
                                />
                            </div>
                            <div style={{marginTop: '-24px'}}>
                                <OdxSelect
                                    options={getCurrentDoopAreasOverview()}
                                    value={areas}
                                    id={`${baseId}-area-filter`}
                                    onChange={(selectedValues) => {
                                        setAreas(Array.isArray(selectedValues) ? selectedValues : []);
                                    }}
                                    placeholder="Area"
                                    isMulti={true}
                                />
                            </div>
                            <div style={{marginTop: '-7px'}}>
                                <OdxInputField
                                    placeholder="From Date"
                                    isRequired={false}
                                    id={`${baseId}-from-date`}
                                    label="From"
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div style={{marginTop: '-7px'}}>
                                <OdxInputField
                                    placeholder="To Date"
                                    isRequired={false}
                                    id={`${baseId}-to-date`}
                                    label="To"
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                            <div style={{marginTop: '-24px'}}>
                                <odx-icon-button
                                    variant="ghost"
                                    size="sm"
                                    id={`${baseId}-reset-btn`}
                                    icon={`${odxIconSets.core}::reset`}
                                    onClick={() => resetView()}
                                />
                            </div>
                            <div style={{marginTop: '-24px'}}>
                                <odx-icon-button
                                    variant="ghost"
                                    size="sm"
                                    id={`${baseId}-download-btn`}
                                    icon={`${odxIconSets.core}::download`}
                                    onClick={() => downloadView()}
                                />
                            </div>
                        </div>

                    </div>
                </OdxMainFilterGroup>

                <OdxTable
                    id={`${baseId}-table`}
                    headerData={[
                        {name: 'name', title: 'Name', sortable: true},
                        {name: 'id', title: 'ID', sortable: true, className: 'text-center'},
                        {name: 'estimatedHours', title: 'Estimate', sortable: true, className: 'text-center'},
                        {name: 'priceModel', title: 'Price Model', sortable: true, className: 'text-center'},
                        {name: 'orderNumber', title: 'Order Number', sortable: true, className: 'text-center'},
                        {name: 'area', title: 'Area', sortable: true, className: 'text-center'},
                        {name: 'journeyState', title: 'State', sortable: true, className: 'text-center'},
                        {name: 'dateDone', title: 'Acceptance Date', sortable: true, className: 'text-center'},
                        {name: 'dateModified', title: 'Last Modified', sortable: true, className: 'text-center'},
                        {name: 'logs', title: 'Logs', sortable: false, className: 'text-center', required: true},
                    ]}
                >
                    {filteredProjects().map((item, index) => (
                        <OdxTableRow key={index} id={`${baseId}-row-${index}`}>
                            <OdxTableCell>{item.name}</OdxTableCell>
                            <OdxTableCell className="text-center">
                                <a
                                    className="odx-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={'https://dev.azure.com/draeger/_workitems/edit/' + item.id}
                                >
                                    {item.id}
                                </a>
                            </OdxTableCell>
                            <OdxTableCell className="text-center">{item.estimatedHours} h</OdxTableCell>
                            <OdxTableCell className="text-center">{item.priceModel}</OdxTableCell>
                            <OdxTableCell className="text-center">{item.orderNumber}</OdxTableCell>
                            <OdxTableCell className="text-center">{item.area}</OdxTableCell>
                            <OdxTableCell className="text-center">{item.journeyState}</OdxTableCell>
                            <OdxTableCell className="text-center">{trimDate(item.dateDone)}</OdxTableCell>
                            <OdxTableCell className="text-center">{trimDate(item.dateModified)}</OdxTableCell>
                            <OdxTableCell className="text-center">
                                <odx-icon-button
                                    variant="ghost"
                                    id={`${baseId}-row-${index}-logs-btn`}
                                    icon={`${odxIconSets.core}::chevron-right`}
                                    onClick={() => LogPopupShow(item.logs)}
                                />
                            </OdxTableCell>
                        </OdxTableRow>
                    ))}
                </OdxTable>
                <div className="mt-4">Sum of all shown entries: {sumOfAllShownProjects()} h</div>
            </div>
        </div>
    );
};

export default Overview;
